import { useMemo } from "react";
import { cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { PortalNavbar } from "./PortalNavbar";
import { PortalSidebar } from "./PortalSidebar";
import { BlockRenderer, PopupBlock } from "./blocks";
import { GRID_CONTAINER_CLASSES } from "../../shared/schema";
import type { Page, PortalSchema, PopupBlock as PopupBlockType } from "../../shared/schema";

interface PortalContentProps {
  schema: PortalSchema;
  currentPage: Page | null;
  onNavigate: (pageId: string) => void;
  onLogout?: () => void;
  logoURL?: string;
  organizationName?: string;
  /**
   * When true, shows sidebar navigation on larger screens.
   * When false, always shows the hamburger menu navbar.
   */
  showSidebar?: boolean;
  /**
   * When true, renders navigation items as <Link> components for native link behavior.
   * When false, renders as buttons that call onNavigate callback.
   */
  useLinks?: boolean;
  /** Empty state message when no blocks are on the page */
  emptyMessage?: string;
}

/**
 * Shared portal content renderer used by both the enduser portal and admin preview.
 * Handles conditional rendering of sidebar vs navbar based on settings and screen size.
 */
export const PortalContent: React.FC<PortalContentProps> = ({
  schema,
  currentPage,
  onNavigate,
  onLogout,
  logoURL,
  organizationName,
  showSidebar = false,
  useLinks = false,
  emptyMessage,
}) => {
  const { t } = useTranslation();

  const defaultEmptyMessage = t("No content available on this page.");

  // Collect global popups from all pages (popups with showOnAllPages: true)
  const globalPopups = useMemo(() => {
    const popups: PopupBlockType[] = [];
    for (const page of schema.pages) {
      for (const block of page.blocks) {
        if (block.type === "popup" && block.props.showOnAllPages) {
          popups.push(block);
        }
        // Also check inside sections
        if (block.type === "section") {
          for (const childBlock of block.blocks) {
            if (childBlock.type === "popup" && childBlock.props.showOnAllPages) {
              popups.push(childBlock);
            }
          }
        }
      }
    }
    return popups;
  }, [schema.pages]);

  return (
    <div className="h-[100dvh] bg-background flex">
      {/* Global popups - render outside page content since they're modals */}
      {globalPopups.map((popup) => (
        <PopupBlock key={popup.id} block={popup} isGlobalRender />
      ))}
      {/* Sidebar - only when enabled */}
      {showSidebar && (
        <PortalSidebar
          pages={schema.pages}
          currentPage={currentPage}
          onNavigate={onNavigate}
          portalName={schema.name}
          onLogout={onLogout}
          logoURL={logoURL}
          organizationName={organizationName}
          useLinks={useLinks}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-full">
        {/* Navbar - hidden when sidebar is shown */}
        {!showSidebar && (
          <PortalNavbar
            pages={schema.pages}
            currentPage={currentPage}
            onNavigate={onNavigate}
            portalName={schema.name}
            onLogout={onLogout}
            logoURL={logoURL}
            organizationName={organizationName}
            useLinks={useLinks}
          />
        )}

        {/* Portal content - renders current page blocks in a responsive grid */}
        <div className="flex-1 overflow-auto p-6">
          <div className={cn("max-w-4xl mx-auto", GRID_CONTAINER_CLASSES)}>
            {currentPage?.blocks.map((block) => <BlockRenderer key={block.id} block={block} />)}
            {(!currentPage || currentPage.blocks.length === 0) && (
              <div className="col-span-12 text-center py-12 text-muted-foreground">
                <p>{emptyMessage ?? defaultEmptyMessage}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
